import { useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, ArrowRight, Pencil } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useTrip, useUI } from "@/lib/store";
import { fmtLong, fmtTime, isoOf } from "@/lib/dates";
import { StatusBadge } from "./StatusBadge";

export function computeNext(trip, today) {
  const now = new Date();
  const nowTime = isoOf(now) === today ? `${String(now.getHours()).padStart(2, "0")}:${String(now.getMinutes()).padStart(2, "0")}` : "00:00";
  const items = [
    ...trip.transports.map((t) => ({ kind: "transport", id: t.id, date: t.date, time: t.departTime, title: t.title, sub: [t.carrier, t.duration && `Approx. ${t.duration}`].filter(Boolean).join(" · "), status: t.status, missing: t.missing, place: t.toPlace || t.toCity, to: "/more/transport" })),
    ...trip.hotels.map((h) => ({ kind: "hotel", id: h.id, date: h.checkIn, time: null, title: `Check in · ${h.name}`, sub: `${h.city} · ${h.nights} nights`, status: h.status, place: h.address, to: "/more/stays", hotel: h })),
    ...trip.events.filter((e) => !e.completed && !e.private).map((e) => ({ kind: "event", id: e.id, date: e.date, time: e.startTime, title: e.title, sub: e.city, status: e.status, place: e.address, to: `/trip?date=${e.date}` })),
  ];
  const sorted = items.sort((a, b) => (a.date + (a.time || "00:01") + (a.kind === "transport" ? "0" : "1")).localeCompare(b.date + (b.time || "00:01") + (b.kind === "transport" ? "0" : "1")));
  return sorted.find((i) => i.date > today || (i.date === today && (!i.time || i.time >= nowTime))) || sorted.find((i) => i.date === today) || null;
}

export function NextUpCard({ item, onClose }) {
  const navigate = useNavigate();
  const { organiser } = useTrip();
  const { showDriver } = useUI();
  if (!item) return <div data-testid="next-up-empty" className="card-soft p-5 text-sm text-[var(--ink-muted)]">The adventure is complete — nothing further on the itinerary.</div>;
  return (
    <div data-testid="next-up-card" className="relative overflow-hidden rounded-3xl bg-[var(--jade)] p-5 text-[var(--parchment)]">
      <div className="grain absolute inset-0" />
      <div className="relative">
        <div className="flex items-center justify-between">
          <p className="eyebrow !text-[var(--brass)]">Next up</p>
          {onClose && <button data-testid="whats-next-close" onClick={onClose} className="tap rounded-full bg-white/10 p-1.5"><X className="h-4 w-4" /></button>}
        </div>
        <h3 data-testid="next-up-title" className="font-display mt-1 text-3xl font-semibold leading-tight">{item.title}</h3>
        <p className="mt-1 text-sm text-[var(--parchment)]/80">{fmtLong(item.date)}{item.time ? ` · ${fmtTime(item.time)}` : ""}</p>
        {item.sub && <p className="mt-2 text-sm">{item.sub}</p>}
        {item.place && <p className="text-sm text-[var(--parchment)]/70">{item.place}</p>}
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <StatusBadge status={item.status} />
          {item.kind === "transport" && !item.time && <span className="text-xs text-[var(--parchment)]/80">Departure time: TBC</span>}
        </div>
        <div className="mt-4 flex flex-wrap gap-2">
          <button data-testid="next-up-open-btn" onClick={() => { onClose?.(); navigate(item.to); }} className="tap inline-flex items-center gap-1.5 rounded-full bg-[var(--parchment)] px-4 py-2 text-sm font-semibold text-[var(--jade)]">Open <ArrowRight className="h-4 w-4" /></button>
          {item.hotel && <button data-testid="next-up-driver-btn" onClick={() => { onClose?.(); showDriver(item.hotel); }} className="tap rounded-full border border-[var(--parchment)]/40 px-4 py-2 text-sm font-semibold">Show driver</button>}
          {organiser && item.missing?.length > 0 && <button data-testid="next-up-complete-btn" onClick={() => { onClose?.(); navigate("/more/transport"); }} className="tap inline-flex items-center gap-1.5 rounded-full bg-[var(--brass)] px-4 py-2 text-sm font-semibold text-[var(--jade-deep)]"><Pencil className="h-3.5 w-3.5" /> Complete details</button>}
        </div>
      </div>
    </div>
  );
}

export function WhatsNextSheet() {
  const { whatsNext, setWhatsNext } = useUI();
  const { trip, today } = useTrip();
  const item = useMemo(() => (trip ? computeNext(trip, today) : null), [trip, today]);
  return (
    <AnimatePresence>
      {whatsNext && (
        <motion.div className="fixed inset-0 z-[60] flex items-end justify-center bg-black/40 p-4 md:items-center" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={() => setWhatsNext(false)}>
          <motion.div data-testid="whats-next-sheet" initial={{ y: 60 }} animate={{ y: 0 }} exit={{ y: 60 }} className="w-full max-w-md" onClick={(e) => e.stopPropagation()}>
            <NextUpCard item={item} onClose={() => setWhatsNext(false)} />
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
